// Per-location team: an owner invites teammates who then get backoffice access
// to that single location. Rentals is the only per-member permission, and it is
// capped by the owner's own rentals feature.

export enum LocationRole {
  OWNER = 'owner',
  MEMBER = 'member',
}

export enum TeamMemberStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
}

export interface TeamMemberUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface TeamMember {
  id: number;
  status: TeamMemberStatus;
  // The member's own flag. `effectiveRentalEnabled` is it ANDed with the
  // owner's feature — gate UI on the effective value, never recombine them.
  rentalEnabled: boolean;
  effectiveRentalEnabled: boolean;
  invitedAt: string;
  acceptedAt: string | null;
  inviteExpiresAt: string | null;
  user?: TeamMemberUser;
}

export interface TeamOwner {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface TeamResponse {
  // The owner is not a membership row, so the API returns them separately.
  owner?: TeamOwner;
  // The ceiling: when false no teammate of this location can have rentals.
  ownerRentalEnabled: boolean;
  members: TeamMember[];
}

// Someone the caller already works with on another location, so inviting them
// to a second site is a pick rather than a retyped email.
export interface KnownTeammate {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  locationCount: number;
}

export interface InvitePayload {
  email?: string;
  userId?: number;
  rentalEnabled?: boolean;
}

// Public invitation-acceptance payloads (the emailed token is the credential).
export interface TeamInvitation {
  locationName: string;
  ownerName: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  // A brand-new invitee still has to choose a password.
  requiresPassword: boolean;
}

export interface AcceptInvitationPayload {
  firstName?: string;
  lastName?: string;
  password?: string;
}

export interface AcceptInvitationResponse {
  code: 'INVITE_ACCEPTED' | 'INVITE_ACCEPTED_LOGIN_REQUIRED';
  // Only returned for a brand-new user who just set a password: an existing
  // account is never auto-signed-in from an emailed link.
  accessToken?: string;
  refreshToken?: string;
}
